import { AppSettings } from "../../app-settings";
import {SearchModel} from "../models/search";
import {SearchAlertModel} from "../models/search-alert";
import {AuthorizationModel} from "../../authentication/models/authorization-model";

import { Collection } from "backbone-associations";
import {backboneViewProperties} from "../../decorators/backbone-properties";
import { Container, autoinject } from "aurelia-dependency-injection";
import { UserSettingsModel } from "../../settings/models/user-settings";
import { AppEvents } from "../../app-events";
import { AuthService } from "../../authentication/auth-service";
import { AppUser } from "../../authentication/app-user";

require("../styles/alert-panel.less");

const template = require<string>("./search-alert.html");
const dom = {
    CONTROLS_REGION: ".controls-region",
    TEXT_REGION: ".text-region",
    ENABLE_REGION: ".enable-region",
    CONFIRM_REGION: ".confirm-region",
    ALERT_NAME: ".alert-name",
    ALERT_EMAIL_ADDRESS: ".alert-email",
    ALERT_EMAIL_SUBJECT: ".alert-email-subject",
    ALERT_ENABLE_BUTTON: ".alert-enable-button",
    ALERT_WARNING: ".alert-warning"
};

@backboneViewProperties({
    template: template,
    tagName: "div",
    className: "clearfix",
    id: "alert-panel",
})

@autoinject
export class SearchAlertView extends Marionette.LayoutView<SearchAlertModel> {

    public model: SearchAlertModel;
    private user: AppUser;

    constructor(private searchModel: SearchModel,
                private appEvents: Backbone.Events,
                private authService: AuthService) {
        super();

        this.user = authService.AppUser;
        this.model = new SearchAlertModel();
        this.setupModelListeners();
    }

    public setupModelListeners() {
        this.listenTo(this.user, "change:Sku", this.setAlertButtonAvailability);
    }

    public onShow() {
        this.init();
        this.setAlertButtonAvailability();
    }

    public setAlertButtonAvailability() {
        if (this.user.IsTrialist) {
            this.disableAlertButton("Alerts not available to trial users");
        } else if (this.user.Sku.toLowerCase() === "free" || this.user.Sku.toLowerCase() === "open") {
            this.disableAlertButton("Alerts not available to free or open users");
        } else if (this.user.AuthenticatedByIp) {
            this.disableAlertButton("Alerts not available to IP Recognised users");
        } else if (!this.user.Email) {
            this.disableAlertButton("Email is required to create an alert");
        } else {
            this.enableAlertButton();
        }
    }

    public init(): void {
        this.model.name = `Search: ${this.searchModel.getQueryString()}`;
        this.model.subject = `JustisOne Alert - ${this.model.name}`;
        this.model.frequency = "Daily";
        this.model.recipient = (this.user.Email && !this.user.isAcademicUser) ? this.user.Email : "N/A";

        this.$(dom.ALERT_NAME).val(this.model.name) ;
        this.$(dom.ALERT_EMAIL_SUBJECT).val(this.model.subject);
        this.$(dom.ALERT_EMAIL_ADDRESS).text(this.model.recipient);
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};

        events["keyup .alert-name"] = (e) => this.onKeyupAlertName(e);
        events["keyup .alert-email-subject"] = (e) => this.onKeyupEmailSubject(e);
        events["click .alert-enable-button"] = (e) => this.onClickEnableButton(e);
        events["change #alert-frequency-select"] = (e) => this.onChangeFrequency(e);

        return events;
    }

    protected onKeyupAlertName(e: JQueryEventObject): void  {
        const val = $(e.target).val();
        this.onBlurAlert(e, val);
        this.model.name = val;
    }

    protected onKeyupEmailSubject(e: JQueryEventObject): void  {
        const val = $(e.target).val();
        this.onBlurAlert(e, val);
        this.model.subject = val;
    }

    protected onBlurAlert(e: JQueryEventObject, val: string): void  {
        if (val === "") {
            this.$(dom.ALERT_WARNING).text("* Alert name and email subject must not be empty.").show();
            this.$(".alert-enable-button").addClass("disabled");
        } else {
            if (this.$(dom.ALERT_NAME).val() === "" || this.$(dom.ALERT_EMAIL_SUBJECT).val() === "") {
                this.$(dom.ALERT_WARNING).text("* Alert name and email subject must not be empty.").show();
                this.$(".alert-enable-button").addClass("disabled");
            } else {
                this.$(dom.ALERT_WARNING).text("").hide();
                this.$(".alert-enable-button").removeClass("disabled");
            }
        }
    }

    protected onClickEnableButton(e: JQueryEventObject): void  {
        const $button = $(e.currentTarget);
        if ($button.hasClass("disabled") || $button.hasClass("disallowed") || $button.hasClass("thinking")) {
            return;
        }
        $button.addClass("thinking");
        const queryData = this.searchModel.getSearchQueryData();

        // Get the latest advanced search form data
        if (this.searchModel.AdvancedVisible) {
            queryData.MaterialType = this.searchModel.AdvancedSearch.MaterialType;
            queryData.Indicators = this.searchModel.getActiveIndicators();
        }

        this.model.search = queryData;
        this.searchModel.getUserSettingsForServer().then((response) => {
            this.model.search.CurrentJurisdictionSettings = response;
        });
        this.model.enabled = true;

        this.appEvents.trigger(AppEvents.AppInsightsAlertChange, {Type: "Search", Value: this.model.name, Enabled: this.model.enabled});

        this.model.saveSearchAlert().then(() => {
            $button.removeClass("thinking");
            this.hidePanels();
            $(dom.CONFIRM_REGION).text("Your alert has been saved");
        }, () => {
            $button.removeClass("thinking");
            this.appEvents.trigger(AppEvents.ErrorMessage, "Unable to save alert.");
        }).catch(() => {
            $button.removeClass("thinking");
            this.appEvents.trigger(AppEvents.ErrorMessage, "Unable to save alert.");
        });
    }

    protected hidePanels() {
        $(dom.CONTROLS_REGION).hide();
        $(dom.TEXT_REGION).hide();
        $(dom.ENABLE_REGION).hide();
    }

    protected onChangeFrequency(e: JQueryEventObject): void  {
        this.model.frequency = $(e.target).val();
    }

    private disableAlertButton(message: string) {
        const $label = this.$(dom.ALERT_ENABLE_BUTTON).find(".btn-text.show-label");
        const $icon = this.$(dom.ALERT_ENABLE_BUTTON).find("i");

        this.$(dom.ALERT_ENABLE_BUTTON).addClass("disallowed");
        this.$(dom.ALERT_ENABLE_BUTTON).attr("title", message);
        this.$(dom.ALERT_WARNING).text("* " + message).show();
        $label.text(" Alerts Disabled");
        $icon.attr("class", "fa fa-ban");
    }

    private enableAlertButton() {
        const $label = this.$(dom.ALERT_ENABLE_BUTTON).find(".btn-text.show-label");
        const $icon = this.$(dom.ALERT_ENABLE_BUTTON).find("i");

        this.$(dom.ALERT_ENABLE_BUTTON).removeClass("disallowed");
        this.$(dom.ALERT_ENABLE_BUTTON).attr("title", "");
        this.$(dom.ALERT_WARNING).text("").hide();
        $label.text(" Create Alert");
        $icon.attr("class", "fa fa-bell");
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/views/search-alert.ts